/**
 * Card Library drawer (founder 2026-06-12): browse EVERY card in the product,
 * grouped the same way as the sidebar, with search. Cards that render on the
 * Exec page can be added/removed from the layout right here; cards whose home
 * is another page deep-link to it (every number has ONE home — IA decision).
 *
 * Locked cards (missing data source) stay visible with a "needs …" chip —
 * founder's completeness rule: visibly-disabled-with-reason, never hidden.
 * Adding a locked card is allowed; it renders with its unlock notice + CTA.
 */

import { useEffect, useMemo, useState } from "react";
import {
  CARD_CATALOG, CATEGORY_ORDER, CAPABILITY_SHORT, ROUTE_LABELS,
  type CatalogCard, type CardChart, type CardPeriod,
} from "../lib/card-catalog.js";
import { firstMissing, type Capabilities } from "../lib/card-requirements.js";
import { WIDGETS } from "../lib/dashboard-widgets.js";

/** Catalog ↔ WIDGETS drift guard (Codex review): an entry whose widgetId no
 * longer exists would add a dead id that readLayout() silently filters out —
 * the card would "add" and never appear. Only offer +Add for real widgets,
 * and complain loudly in dev when the two files disagree. */
const WIDGET_IDS = new Set(WIDGETS.map((w) => w.id));
if (import.meta.env.DEV) {
  for (const c of CARD_CATALOG) {
    if (c.widgetId !== undefined && !WIDGET_IDS.has(c.widgetId)) console.warn(`card-catalog: widgetId "${c.widgetId}" has no WIDGETS entry`);
  }
  for (const w of WIDGETS) {
    if (!CARD_CATALOG.some((c) => c.widgetId === w.id)) console.warn(`card-catalog: WIDGETS id "${w.id}" is missing a catalog entry`);
  }
}

const CHART_LABELS: Record<CardChart, string> = {
  number: "Number", line: "Line", bars: "Bars", "stacked-bars": "Stacked bars",
  ranked: "Ranked bars", donut: "Donut", matrix: "Matrix", map: "Map",
  table: "Table", strip: "Journey",
};
const PERIOD_LABELS: Record<CardPeriod, string> = {
  picker: "follows date picker", "all-time": "all time", monthly: "by month",
  "30-day": "last 30 days", live: "right now",
};

export function CardLibrary({ open, onClose, layout, onAdd, onRemove, caps, onNavigate }: {
  open: boolean;
  onClose: () => void;
  /** Current Exec layout — drives the +Add / ✓Added state. */
  layout: string[];
  onAdd: (widgetId: string) => void;
  onRemove: (widgetId: string) => void;
  caps?: Capabilities;
  onNavigate: (route: string) => void;
}): React.ReactElement | null {
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus();
    };
  }, [open, onClose]);

  const needle = q.trim().toLowerCase();
  const matches = useMemo(
    () =>
      CARD_CATALOG.filter((c) => {
        return needle === "" ||
          c.title.toLowerCase().includes(needle) ||
          c.blurb.toLowerCase().includes(needle) ||
          c.category.toLowerCase().includes(needle);
      }),
    [needle],
  );

  if (!open) return null;

  const ready = matches;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 80 }} />
      <div role="dialog" aria-modal="true" aria-label="Card library" style={drawer}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Card library</div>
            <div style={{ fontSize: 12, color: "var(--body)", marginTop: 3 }}>
              Every card in the product. Add the ones you want to your Exec page — cards that live on another page link through.
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={closeBtn}>✕</button>
        </div>

        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${CARD_CATALOG.length} cards…`}
          style={searchStyle}
        />

        {CATEGORY_ORDER.map((cat) => {
          const cards = ready.filter((c) => c.category === cat);
          if (cards.length === 0) return null;
          return (
            <div key={cat} style={{ marginTop: 18 }}>
              <div style={catHead}>{cat} <span style={{ color: "var(--faint)", fontWeight: 400 }}>· {cards.length}</span></div>
              {cards.map((c) => (
                <LibraryRow key={c.id} card={c} layout={layout} caps={caps}
                  onAdd={onAdd} onRemove={onRemove}
                  onGo={(route) => { onNavigate(route); onClose(); }} />
              ))}
            </div>
          );
        })}

        {ready.length === 0 && (
          <div style={{ color: "var(--body)", fontSize: 13, marginTop: 20 }}>No cards match “{q}”.</div>
        )}
      </div>
    </>
  );
}

function LibraryRow({ card, layout, caps, onAdd, onRemove, onGo }: {
  card: CatalogCard;
  layout: string[];
  caps?: Capabilities;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  onGo: (route: string) => void;
}): React.ReactElement {
  const addable = card.widgetId !== undefined && WIDGET_IDS.has(card.widgetId);
  const added = addable && layout.includes(card.widgetId as string);
  const missing = firstMissing(card.requires, caps);
  return (
    <div style={rowStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>{card.title}</div>
        <div style={{ fontSize: 12, color: "var(--body)", marginTop: 3, lineHeight: 1.45 }}>{card.blurb}</div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
          <span style={chip}>{CHART_LABELS[card.chart]}{card.chartSwitch ? " ⇄" : ""}</span>
          <span style={chip}>{PERIOD_LABELS[card.period]}</span>
          {card.drill && <span style={chip}>↗ drills in</span>}
          {missing && <span style={{ ...chip, color: "var(--bad)", borderColor: "var(--bad)" }}>needs {CAPABILITY_SHORT[missing]}</span>}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
        {addable && (
          added ? (
            <button onClick={() => onRemove(card.widgetId as string)} title="Click to remove from your Exec page" style={{ ...actBtn, borderColor: "var(--accent)", color: "var(--accent)" }}>✓ Added</button>
          ) : (
            <button onClick={() => onAdd(card.widgetId as string)} style={actBtn}>+ Add</button>
          )
        )}
        {card.route !== undefined && (
          <button onClick={() => onGo(card.route as string)} style={{ ...actBtn, border: "none", background: "transparent", color: "var(--accent)", paddingRight: 0 }}>
            {ROUTE_LABELS[card.route] ?? card.route} →
          </button>
        )}
      </div>
    </div>
  );
}

const drawer: React.CSSProperties = {
  position: "fixed", top: 0, right: 0, bottom: 0, width: "min(420px, 94vw)",
  background: "var(--surface)", borderLeft: "1px solid var(--border)",
  boxShadow: "var(--shadow-pop)", zIndex: 81, overflowY: "auto", padding: "18px 18px 28px",
};
const closeBtn: React.CSSProperties = {
  border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--body)",
  borderRadius: 8, width: 28, height: 28, cursor: "pointer", fontSize: 13, lineHeight: 1, flexShrink: 0,
};
const searchStyle: React.CSSProperties = {
  width: "100%", marginTop: 14, border: "1px solid var(--border)", background: "var(--surface-2)",
  color: "var(--text)", borderRadius: "var(--r-sm)", padding: "9px 12px", fontSize: 13, boxSizing: "border-box",
};
const catHead: React.CSSProperties = {
  fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em",
  color: "var(--faint)", fontWeight: 700, margin: "0 0 4px",
};
const rowStyle: React.CSSProperties = {
  display: "flex", gap: 12, alignItems: "flex-start", padding: "11px 10px",
  border: "1px solid var(--border)", borderRadius: "var(--r-md)", background: "var(--surface-2)", marginTop: 8,
};
const chip: React.CSSProperties = {
  display: "inline-block", fontSize: 10.5, color: "var(--faint)", border: "1px solid var(--border)",
  borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap",
};
const actBtn: React.CSSProperties = {
  border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)",
  borderRadius: "var(--r-sm)", padding: "6px 11px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
};
